import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  FormRow,
  Input,
  Modal,
  Text
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import {
  Authentication,
  AuthUpdatePayload,
  GovOsResult
} from '../api/govos.types';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache,
  invalidateServiceTemplatesCache
} from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';
import { useTemplateNameValidation } from '../hooks/useTemplateNameValidation';

interface EditSchriftformConfigModalProps {
  startAppId: string | number;
  processId?: string | number;
  templateId?: string | number;
  authentication?: Authentication;
  onClose: () => void;
  isOpen: boolean;
  isTemplatePage?: boolean;
  templateName?: string;
  existingNames?: string[];
}

interface SchriftformFormData {
  schriftformNotRequired: boolean;
  hasHybrid: boolean;
  hasSignature: boolean;
  hasEid: boolean;
  hasSubstantiel: boolean;
  hasEidLogin: boolean;
}

function getDefaultValues(auth?: Authentication): SchriftformFormData {
  return {
    schriftformNotRequired: auth?.schriftformNotRequired ?? false,
    hasHybrid: auth?.withHybrid ?? false,
    hasSignature: auth?.withSignature ?? false,
    hasEid: auth?.withEid ?? false,
    hasSubstantiel: auth?.withSubstantiel ?? false,
    hasEidLogin: auth?.withEidLogin ?? false
  };
}

export function EditSchriftformConfigModal({
  startAppId,
  processId,
  templateId,
  authentication,
  onClose,
  isOpen,
  isTemplatePage,
  templateName,
  existingNames
}: EditSchriftformConfigModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const CustomJoi = createJoiWithTranslations(t);

  const validationSchema = CustomJoi.object<SchriftformFormData>({
    schriftformNotRequired: CustomJoi.boolean().optional(),
    hasHybrid: CustomJoi.boolean().optional(),
    hasSignature: CustomJoi.boolean().optional(),
    hasEid: CustomJoi.boolean().optional(),
    hasSubstantiel: CustomJoi.boolean().optional(),
    hasEidLogin: CustomJoi.boolean().optional()
  });

  const { control, handleSubmit, watch } = useForm<SchriftformFormData>({
    defaultValues: getDefaultValues(authentication),
    resolver: joiResolver(validationSchema),
    mode: 'onBlur'
  });

  const hasHybrid = watch('hasHybrid');
  const isSchriftformNotRequired = watch('schriftformNotRequired');
  const hasSignature = watch('hasSignature');
  const hasEid = watch('hasEid');
  const hasSubstantiel = watch('hasSubstantiel');
  const hasEidLogin = watch('hasEidLogin');

  const hasAnySchriftformOption =
    hasHybrid || hasSignature || hasEid || hasSubstantiel || hasEidLogin;
  const isTemplateMode = templateId != null;
  const [shouldSaveAsTemplate, setShouldSaveAsTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateNameInModal, setTemplateNameInModal] = useState(
    templateName ?? ''
  );

  const { templateNameError, validateTemplateName, clearTemplateNameError } =
    useTemplateNameValidation({
      currentTemplateName: isTemplatePage ? templateName : undefined,
      existingNames,
      t,
      validationKeyPrefix: 'EditSchriftformConfigModal'
    });

  const { mutate: createTemplate } = useMutation({
    mutationFn: async (configuration: string) =>
      notifyUserFromRequest(
        () =>
          govosClient.post(GovosApiRoutes.serviceTemplates(startAppId), {
            name: newTemplateName,
            serviceType: 'schriftform',
            configuration
          }),
        {
          successMessage: t('api.saveAsTemplate.successMessage'),
          errorMessage: t('api.saveAsTemplate.errorMessage')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateServiceTemplatesCache()
      });
    }
  });

  const { mutate: updateAuthentication, isPending } = useMutation({
    mutationFn: async (payload: AuthUpdatePayload) =>
      notifyUserFromRequest(
        () =>
          isTemplateMode
            ? govosClient.put(
                GovosApiRoutes.serviceTemplate(startAppId, templateId),
                {
                  name: isTemplatePage ? templateNameInModal : undefined,
                  configuration: JSON.stringify(payload)
                }
              )
            : govosClient.put<GovOsResult<unknown>>(
                GovosApiRoutes.authenticationService(startAppId, processId!),
                payload
              ),
        {
          successMessage: t('EditSchriftformConfigModal.successMessage'),
          errorMessage: t('EditSchriftformConfigModal.errorMessage'),
          loadingMessage: t('EditSchriftformConfigModal.loadingMessage')
        }
      ),
    onSuccess: async (_, payload) => {
      if (shouldSaveAsTemplate && newTemplateName?.trim() && !isTemplateMode) {
        createTemplate(JSON.stringify(payload));
      }
      if (isTemplateMode) {
        await queryClient.invalidateQueries({
          predicate: invalidateServiceTemplatesCache()
        });
      } else {
        await queryClient.invalidateQueries({
          predicate: invalidateConfiguredOnlinediensteCache(processId ?? '')
        });
      }
      onClose();
    }
  });

  const handleSave = handleSubmit((data) => {
    if (!data.schriftformNotRequired && !hasAnySchriftformOption) {
      return;
    }
    if (shouldSaveAsTemplate && !isTemplateMode) {
      if (!validateTemplateName(newTemplateName)) {
        return;
      }
    }
    if (isTemplatePage && isTemplateMode) {
      if (!validateTemplateName(templateNameInModal)) {
        return;
      }
    }
    const payload: AuthUpdatePayload = {
      schriftformNotRequired: data.schriftformNotRequired,
      withHybrid: data.hasHybrid,
      withSignature: data.hasSignature,
      withEid: data.hasEid,
      withSubstantiel: data.hasSubstantiel,
      withEidLogin: data.hasEidLogin
    };
    updateAuthentication(payload);
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditSchriftformConfigModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <Text size="m" strong>
            {t('EditSchriftformConfigModal.description')}
          </Text>

          {/* Schriftformerfordernis options */}
          <FormRow label={t('EditSchriftformConfigModal.sections.schriftform')}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Controller
                name="schriftformNotRequired"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                    }}
                    label={t(
                      'EditSchriftformConfigModal.fields.schriftformNotRequired'
                    )}
                    disabled={hasAnySchriftformOption}
                  />
                )}
              />
              <Controller
                name="hasHybrid"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                    }}
                    label={t('EditSchriftformConfigModal.fields.withHybrid')}
                    subline={t(
                      'EditSchriftformConfigModal.fields.withHybridSubline'
                    )}
                    disabled={isSchriftformNotRequired}
                  />
                )}
              />
              <Controller
                name="hasSignature"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                    }}
                    label={t('EditSchriftformConfigModal.fields.withSignature')}
                    subline={t(
                      'EditSchriftformConfigModal.fields.withSignatureSubline'
                    )}
                    disabled={isSchriftformNotRequired}
                  />
                )}
              />
              <Controller
                name="hasEid"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                    }}
                    label={t('EditSchriftformConfigModal.fields.withEid')}
                    subline={t(
                      'EditSchriftformConfigModal.fields.withEidSubline'
                    )}
                    disabled={isSchriftformNotRequired}
                  />
                )}
              />
              <Controller
                name="hasSubstantiel"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                    }}
                    label={t(
                      'EditSchriftformConfigModal.fields.withSubstantiel'
                    )}
                    disabled={isSchriftformNotRequired}
                  />
                )}
              />
              <hr className="fjd2-description-list_divider" />
              <Controller
                name="hasEidLogin"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                    }}
                    label={t('EditSchriftformConfigModal.fields.withEidLogin')}
                    subline={t(
                      'EditSchriftformConfigModal.fields.withEidLoginSubline'
                    )}
                    disabled={isSchriftformNotRequired}
                  />
                )}
              />
            </div>
          </FormRow>

          {isTemplatePage && isTemplateMode && (
            <FormRow
              label={t('EditSchriftformConfigModal.templateName.label')}
              validationMessage={templateNameError}
              variant={templateNameError ? 'error' : undefined}
            >
              <Input
                value={templateNameInModal}
                onChange={(e) => {
                  setTemplateNameInModal(e.target.value);
                  clearTemplateNameError();
                }}
                placeholder={t(
                  'EditSchriftformConfigModal.templateName.placeholder'
                )}
                style={{ width: '100%' }}
              />
            </FormRow>
          )}

          {!isTemplatePage && !isTemplateMode && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Checkbox
                checked={shouldSaveAsTemplate}
                onChange={(e) =>
                  setShouldSaveAsTemplate(e.currentTarget.checked)
                }
                label={t('EditSchriftformConfigModal.saveAsTemplate.checkbox')}
              />
              {shouldSaveAsTemplate && (
                <FormRow
                  label={t(
                    'EditSchriftformConfigModal.saveAsTemplate.nameLabel'
                  )}
                  validationMessage={templateNameError}
                  variant={templateNameError ? 'error' : undefined}
                >
                  <Input
                    value={newTemplateName}
                    onChange={(e) => {
                      setNewTemplateName(e.target.value);
                      clearTemplateNameError();
                    }}
                    placeholder={t(
                      'EditSchriftformConfigModal.saveAsTemplate.namePlaceholder'
                    )}
                    style={{ width: '100%' }}
                  />
                </FormRow>
              )}
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            disabled={isPending}
            style={{ flex: 1 }}
          >
            {t('EditSchriftformConfigModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={
              isPending ||
              (!isSchriftformNotRequired && !hasAnySchriftformOption)
            }
            style={{ flex: 1 }}
          >
            {t('EditSchriftformConfigModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
